(() => {
  const bootstrapKey = '__learningCenterBootstrap';

  if (window[bootstrapKey]?.controller) {
    window[bootstrapKey].controller.scan(document);
    return;
  }

  class LearningCenterController {
    constructor() {
      this.instances = new Map();
      this.dedupeFrame = 0;
      this.onSectionLoad = (event) => {
        this.scan(event.target);
        this.schedulePortalDedupe();
      };
      this.onSectionUnload = (event) => {
        this.cleanup(event.target);
        this.schedulePortalDedupe();
      };
      this.onSectionReorder = () => this.schedulePortalDedupe();
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      document.addEventListener('shopify:section:reorder', this.onSectionReorder);
    }

    scan(scope = document) {
      const roots = [];
      if (scope.matches?.('[data-learning-nav], [data-learning-newsletter], [data-learning-pagination], [data-learning-latest-carousel], [data-learning-subtopic-carousel]')) roots.push(scope);
      roots.push(...scope.querySelectorAll?.('[data-learning-nav], [data-learning-newsletter], [data-learning-pagination], [data-learning-latest-carousel], [data-learning-subtopic-carousel]') || []);
      roots.forEach((root) => {
        if (this.instances.has(root)) return;
        const abortController = new AbortController();
        const options = { signal: abortController.signal };
        if (root.matches('[data-learning-nav]')) this.bindNav(root, options);
        if (root.matches('[data-learning-newsletter]')) this.bindNewsletter(root, options);
        if (root.matches('[data-learning-latest-carousel], [data-learning-subtopic-carousel]')) this.bindCarousel(root, options);
        if (root.matches('[data-learning-pagination]') && this.bindPagination(root, options)) {
          root.classList.add('is-load-more-ready');
          abortController.signal.addEventListener('abort', () => root.classList.remove('is-load-more-ready'), { once: true });
        }
        this.instances.set(root, abortController);
      });
      this.schedulePortalDedupe();
    }

    cleanup(scope) {
      for (const [root, abortController] of this.instances) {
        if (root === scope || scope.contains(root)) {
          abortController.abort();
          this.instances.delete(root);
        }
      }
    }

    destroy() {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      document.removeEventListener('shopify:section:reorder', this.onSectionReorder);
      if (this.dedupeFrame) cancelAnimationFrame(this.dedupeFrame);
      for (const abortController of this.instances.values()) abortController.abort();
      this.instances.clear();
    }

    schedulePortalDedupe() {
      if (this.dedupeFrame) cancelAnimationFrame(this.dedupeFrame);
      this.dedupeFrame = requestAnimationFrame(() => {
        this.dedupeFrame = 0;
        this.dedupePortalArticles();
      });
    }

    dedupePortalArticles() {
      const modules = [...document.querySelectorAll('[data-learning-dedupe-module]')];
      const seen = new Set();

      modules.forEach((module) => {
        if (module.dataset.learningDedupeHidden === 'true') {
          module.hidden = false;
          delete module.dataset.learningDedupeHidden;
        }
        module.classList.remove('is-dedupe-empty');
        delete module.dataset.learningVisibleCount;
        module.querySelector('[data-learning-dedupe-empty]')?.setAttribute('hidden', '');
        module.querySelectorAll('.lc-card[data-article-id]').forEach((card) => {
          card.hidden = false;
          card.removeAttribute('aria-hidden');
          delete card.dataset.learningDuplicate;
          delete card.dataset.learningLimitHidden;
        });
      });

      modules.forEach((module) => {
        const cards = [...module.querySelectorAll('.lc-card[data-article-id]')];
        const visibleLimit = Number.parseInt(module.dataset.learningVisibleLimit || '', 10);
        let retained = 0;
        cards.forEach((card) => {
          const articleId = card.dataset.articleId;
          if (articleId && seen.has(articleId)) {
            card.hidden = true;
            card.setAttribute('aria-hidden', 'true');
            card.dataset.learningDuplicate = 'true';
            return;
          }
          if (Number.isFinite(visibleLimit) && retained >= visibleLimit) {
            card.hidden = true;
            card.setAttribute('aria-hidden', 'true');
            card.dataset.learningLimitHidden = 'true';
            return;
          }
          retained += 1;
          if (articleId) seen.add(articleId);
        });

        module.dataset.learningVisibleCount = String(retained);
        if (cards.length === 0 || retained > 0) return;
        module.classList.add('is-dedupe-empty');
        const status = module.querySelector('[data-learning-dedupe-empty]');
        if (module.dataset.learningDesignMode === 'true') {
          status?.removeAttribute('hidden');
        } else {
          module.hidden = true;
          module.dataset.learningDedupeHidden = 'true';
        }
      });
    }

    bindNav(nav, options) {
      const topicButton = nav.querySelector('.lc-subnav__topics');
      const menu = nav.querySelector('.lc-megamenu');
      const drawer = document.querySelector('[data-drawer="hamburger"]');
      const closeMenu = (restoreFocus = false) => {
        if (!menu || !topicButton) return;
        menu.hidden = true;
        topicButton.setAttribute('aria-expanded', 'false');
        if (restoreFocus) topicButton.focus();
      };

      // The global drawer owns its own state; this nav only requests that it closes.
      drawer?.addEventListener('theme:drawer:open', () => closeMenu(), options);

      topicButton?.addEventListener('click', () => {
        const opening = menu.hidden;
        if (opening && drawer?.classList.contains('drawer--visible')) {
          drawer.dispatchEvent(new CustomEvent('theme:drawer:close', { bubbles: false }));
        }
        menu.hidden = !opening;
        topicButton.setAttribute('aria-expanded', String(opening));
      }, options);

      document.addEventListener('click', (event) => {
        if (menu && !menu.hidden && !nav.contains(event.target)) closeMenu();
      }, options);

      menu?.querySelectorAll('.lc-megamenu__group > button').forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.closest('.lc-megamenu__group');
          const opening = !group.classList.contains('is-open');
          group.classList.toggle('is-open', opening);
          button.setAttribute('aria-expanded', String(opening));
        }, options);
      });

      nav.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu && !menu.hidden) closeMenu(true);
      }, options);
    }

    bindNewsletter(root, options) {
      const toggle = root.querySelector('.lc-privacy-toggle');
      const privacy = root.querySelector('.lc-newsletter__privacy');
      const field = root.querySelector('input[type="email"]');
      const expand = () => {
        if (!privacy || !toggle || !privacy.hidden) return;
        privacy.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      };
      toggle?.addEventListener('click', expand, options);
      field?.addEventListener('focus', expand, options);
      field?.addEventListener('input', expand, options);
    }

    bindCarousel(root, options) {
      const slider = root.querySelector('[data-learning-article-list]');
      const previous = root.querySelector('[data-learning-carousel-prev], .lc-latest-carousel__button--prev');
      const next = root.querySelector('[data-learning-carousel-next], .lc-latest-carousel__button--next');
      if (!slider || !previous || !next) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const getCards = () => [...slider.querySelectorAll('.lc-card:not([hidden])')];
      const updateControls = () => {
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        previous.disabled = slider.scrollLeft <= 1;
        next.disabled = maxScroll === 0 || slider.scrollLeft >= maxScroll - 1;
      };
      const move = (direction) => {
        const card = getCards()[0];
        if (!card) return;
        const styles = window.getComputedStyle(slider);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        slider.scrollBy({
          left: direction * (card.getBoundingClientRect().width + gap),
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      };

      previous.addEventListener('click', () => move(-1), options);
      next.addEventListener('click', () => move(1), options);
      slider.addEventListener('scroll', updateControls, { ...options, passive: true });
      window.addEventListener('resize', updateControls, options);
      updateControls();
    }

    bindPagination(root, options) {
      const button = root.querySelector('.lc-load-more');
      const results = root.closest('.lc-section')?.querySelector('[data-learning-results]');
      if (!button || !results || typeof window.fetch !== 'function') return false;
      const scrollKey = (url = window.location.href) => `learning-category-scroll:${url}`;
      const savedScroll = Number.parseInt(sessionStorage.getItem(scrollKey()) || '', 10);
      if (Number.isFinite(savedScroll)) requestAnimationFrame(() => window.scrollTo({ top: savedScroll }));
      window.addEventListener('popstate', (event) => {
        if (!event.state?.learningPagination) return;
        sessionStorage.setItem(scrollKey(window.location.href), String(event.state.scrollY || 0));
        window.location.assign(window.location.href);
      }, options);
      results.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) sessionStorage.setItem(scrollKey(), String(window.scrollY));
      }, options);
      button.addEventListener('click', async () => {
        const status = root.querySelector('[aria-live]');
        const original = button.textContent;
        let loadedUrl = button.dataset.nextUrl;
        button.disabled = true;
        button.textContent = 'Cargando más artículos…';
        results.setAttribute('aria-busy', 'true');
        try {
          const visited = new Set();
          let cards = [];
          let next;
          while (loadedUrl && cards.length === 0) {
            if (visited.has(loadedUrl)) throw new Error('Pagination loop');
            visited.add(loadedUrl);
            const response = await fetch(loadedUrl, { headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Request failed');
            const page = new DOMParser().parseFromString(await response.text(), 'text/html');
            const incoming = page.querySelector('[data-learning-results]');
            if (!incoming) throw new Error('Results missing');
            cards = [...incoming.children];
            next = page.querySelector('.lc-load-more');
            if (cards.length === 0 && next) loadedUrl = next.dataset.nextUrl;
            else break;
          }
          cards.forEach((card) => results?.append(card));
          history.replaceState({ ...history.state, learningPagination: true, scrollY: window.scrollY }, '');
          history.pushState({ learningPagination: true, scrollY: window.scrollY }, '', loadedUrl);
          sessionStorage.setItem(scrollKey(window.location.href), String(window.scrollY));
          if (next) {
            button.dataset.nextUrl = next.dataset.nextUrl;
            button.disabled = false;
            button.textContent = original;
            status.textContent = `Se cargaron ${cards.length} artículos más.`;
          } else {
            button.remove();
            status.textContent = 'Has llegado al final de esta categoría.';
          }
        } catch (_) {
          button.disabled = false;
          button.textContent = 'Reintentar';
          status.textContent = 'No pudimos cargar más artículos. Inténtalo de nuevo.';
        } finally {
          results.setAttribute('aria-busy', 'false');
        }
      }, options);
      return true;
    }
  }

  window.learningCenterController?.destroy?.();
  const controller = new LearningCenterController();
  window[bootstrapKey] = { controller };
  window.learningCenterController = controller;
  controller.scan(document);
})();
